import {
  Table,
  TableColumnsType,
  TableProps,
  ConfigProvider,
  Input,
  Popover,
  Checkbox,
  Tooltip,
} from "antd";
import { MouseEvent, ReactNode, useMemo, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import {
  Search,
  FileText,
  Settings,
  RotateCcw,
  GripVertical,
  Pin,
} from "lucide-react";
import type { TablePaginationConfig } from "antd/es/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export interface PaginationStyleConfig {
  /** Padding right for pagination container */
  paddingRight?: string;
  /** Padding left for pagination container */
  paddingLeft?: string;
  /** Padding top for pagination container */
  paddingTop?: string;
  /** Padding bottom for pagination container */
  paddingBottom?: string;
  /** Margin right for pagination container */
  marginRight?: string;
  /** Margin left for pagination container */
  marginLeft?: string;
  /** Margin top for pagination container */
  marginTop?: string;
  /** Margin bottom for pagination container */
  marginBottom?: string;
  /** Custom className for pagination wrapper */
  wrapperClassName?: string;
  /** Custom className for pagination component */
  paginationClassName?: string;
}

export interface AntDTableProps<T = any> {
  /** Table data source */
  data: T[];
  /** Column definitions */
  columns: TableColumnsType<T>;
  /** Unique key for each row */
  rowKey?: string | ((record: T, index?: number) => string);
  /** Loading state */
  loading?: boolean;
  /** Pagination configuration */
  pagination?:
    | {
        pageSize?: number;
        showSizeChanger?: boolean;
        showTotal?: (total: number, range: [number, number]) => string;
        pageSizeOptions?: string[];
        current?: number;
        total?: number;
        onChange?: (page: number, pageSize: number) => void;
        onShowSizeChange?: (current: number, size: number) => void;
        position?: TablePaginationConfig["position"];
      }
    | false;
  /** Pagination style configuration */
  paginationStyle?: PaginationStyleConfig;
  /** Scroll configuration */
  scroll?: {
    x?: number | string | true;
    y?: number | string;
  };
  /** Empty state message */
  emptyText?: string | ReactNode;
  /** Custom className for the table itself */
  className?: string;
  /** Custom className for the outer container */
  containerClassName?: string;
  /** Show bordered table */
  bordered?: boolean;
  /** Show header */
  showHeader?: boolean;
  /** Table size */
  size?: "small" | "middle" | "large";
  /** Row selection configuration */
  rowSelection?: TableProps<T>["rowSelection"];
  /** On row click handler */
  onRow?: (
    record: T,
    index?: number
  ) => {
    onClick?: (event: MouseEvent) => void;
    onDoubleClick?: (event: MouseEvent) => void;
    onContextMenu?: (event: MouseEvent) => void;
    onMouseEnter?: (event: MouseEvent) => void;
    onMouseLeave?: (event: MouseEvent) => void;
  };
  /** Custom locale */
  locale?: {
    emptyText?: string | ReactNode;
  };
  /** Enable sticky header */
  sticky?: boolean | { offsetHeader?: number };
  /** Show column filter inputs */
  showColumnFilter?: boolean;
  /** Callback when filter value changes */
  onFilterChange?: (columnKey: string, value: string) => void;
  /** Background color for table header */
  headerBg?: string;
  /** Primary color for the theme */
  primaryColor?: string;
  /** Show table settings (column visibility, pinning) */
  showSettings?: boolean;
  /** Custom toolbar content rendered on the left of settings */
  toolbarExtra?: ReactNode;
}

/**
 * Reusable Ant Design Table Component - Fintech Edition
 *
 * A high-performance, premium-styled wrapper around Ant Design's Table component
 * optimized for fintech applications with built-in column filtering and enhanced UI.
 */
const AntDTable = <T extends Record<string, any> = any>({
  data,
  columns,
  rowKey = "id",
  loading = false,
  pagination = {
    pageSize: 10,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total} items`,
    pageSizeOptions: ["10", "20", "50", "100"],
    position: ["bottomLeft"],
  },
  paginationStyle,
  scroll = { x: "max-content" },
  emptyText = "No data found",
  className = "",
  containerClassName = "",
  bordered = false,
  showHeader = true,
  size = "middle",
  rowSelection,
  onRow,
  locale,
  sticky,
  showColumnFilter = false,
  onFilterChange,
  headerBg = "#F9FAFB",
  primaryColor = "#66A0CD",
  showSettings = false,
  toolbarExtra,
}: AntDTableProps<T>) => {
  // State for column settings
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [fixedKeys, setFixedKeys] = useState<
    Map<string, "left" | "right" | false>
  >(new Map());

  // Initialize settings from columns
  useEffect(() => {
    const initialVisible = new Set<string>();
    const initialFixed = new Map<string, "left" | "right" | false>();

    columns.forEach((col: any, index: number) => {
      const key = (("dataIndex" in col ? col.dataIndex : col.key) ??
        `col-${index}`) as string;
      initialVisible.add(key);
      if (col.fixed) {
        initialFixed.set(key, col.fixed as "left" | "right");
      }
    });

    setVisibleKeys(initialVisible);
    setFixedKeys(initialFixed);
  }, [columns]);

  // Handle visibility toggle
  const toggleVisibility = (key: string) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(key)) {
      newVisible.delete(key);
    } else {
      newVisible.add(key);
    }
    setVisibleKeys(newVisible);
  };

  // Handle pinning toggle
  const toggleFixed = (key: string, position: "left" | "right") => {
    const newFixed = new Map(fixedKeys);
    const current = newFixed.get(key);
    if (current === position) {
      newFixed.delete(key);
    } else {
      newFixed.set(key, position);
    }
    setFixedKeys(newFixed);
  };

  // Reset settings
  const resetSettings = () => {
    const initialVisible = new Set<string>();
    const initialFixed = new Map<string, "left" | "right" | false>();

    columns.forEach((col: any, index: number) => {
      const key = (("dataIndex" in col ? col.dataIndex : col.key) ??
        `col-${index}`) as string;
      initialVisible.add(key);
      if (col.fixed) {
        initialFixed.set(key, col.fixed as "left" | "right");
      }
    });

    setVisibleKeys(initialVisible);
    setFixedKeys(initialFixed);
  };

  // Transform columns to apply styling and add filter row if needed
  const transformedColumns = useMemo(() => {
    const processed = columns
      .map((col: any, originalIndex: number) => {
        const key = (("dataIndex" in col ? col.dataIndex : col.key) ??
          `col-${originalIndex}`) as string;

        if (!visibleKeys.has(key)) {
          return null;
        }

        // Check if this specific column should have a filter
        const shouldShowFilter =
          showColumnFilter && (col as any).showFilter !== false;

        // Handle the title content with consistent typography
        const columnTitle = col.title;
        const styledTitle = (
          <span className="text-[12px] font-bold uppercase leading-tight tracking-wider text-slate-500">
            {typeof columnTitle === "function"
              ? columnTitle({})
              : (columnTitle as ReactNode)}
          </span>
        );

        // Use dataIndex or key as the identifier for filtering
        const columnKey = (("dataIndex" in col ? col.dataIndex : col.key) ??
          "") as string;

        return {
          ...col,
          fixed: fixedKeys.get(key) ?? undefined,
          onHeaderCell: (record: any) => {
            const existing = col.onHeaderCell ? col.onHeaderCell(record) : {};
            return {
              ...existing,
              className: cn(
                existing.className,
                shouldShowFilter
                  ? "header-with-filter"
                  : "header-without-filter"
              ),
            };
          },
          title: shouldShowFilter ? (
            <div className="flex w-full min-w-0 flex-col gap-2 overflow-hidden py-1.5">
              {styledTitle}
              <div className="group relative w-full">
                {col.filters ? (
                  <Select
                    value=""
                    onValueChange={value =>
                      columnKey && onFilterChange?.(columnKey, value)
                    }
                  >
                    <SelectTrigger className="h-8 w-full rounded-md border-slate-200 bg-white text-sm shadow-sm transition-all hover:border-slate-300 focus:border-primary focus:ring-1 focus:ring-primary/20">
                      <div className="flex items-center gap-1 overflow-hidden">
                        <Search className="h-3 w-3 text-slate-300" />
                        <SelectValue placeholder="Filter..." />
                      </div>
                    </SelectTrigger>
                    <SelectContent usePortal>
                      <SelectItem value="all">All</SelectItem>
                      {(col.filters as any[]).map(filter => (
                        <SelectItem
                          key={String(filter.value)}
                          value={String(filter.value)}
                        >
                          {filter.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Filter..."
                    size="small"
                    prefix={
                      <Search className="h-3 w-3 text-slate-300 transition-colors group-focus-within:text-primary" />
                    }
                    className="h-8 w-full rounded-md border-slate-200 bg-white text-xs shadow-sm transition-all hover:border-slate-300 focus:border-primary focus:ring-1 focus:ring-primary/20"
                    onClick={e => e.stopPropagation()}
                    onChange={e =>
                      columnKey && onFilterChange?.(columnKey, e.target.value)
                    }
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex w-full min-w-0 flex-col overflow-hidden py-1.5">
              {styledTitle}
            </div>
          ),
        };
      })
      .filter(Boolean) as any[];

    // Ant Design requires fixed columns to be at the ends of the columns array
    const leftFixed = processed.filter(c => c.fixed === "left");
    const normal = processed.filter(
      c => !c.fixed || (c.fixed !== "left" && c.fixed !== "right")
    );
    const rightFixed = processed.filter(c => c.fixed === "right");

    return [...leftFixed, ...normal, ...rightFixed];
  }, [columns, showColumnFilter, onFilterChange, visibleKeys, fixedKeys]);

  // Memoize pagination config
  const paginationConfig: TablePaginationConfig | false = useMemo(() => {
    if (pagination === false) {
      return false;
    }

    const currentPagination = pagination as any;
    const pageSizeOptions = currentPagination?.pageSizeOptions ?? [
      "10",
      "20",
      "50",
      "100",
    ];

    return {
      pageSize: currentPagination?.pageSize ?? 10,
      showSizeChanger: false,
      showTotal: (total: number, range: [number, number]) => (
        <div className="flex items-center gap-4">
          {currentPagination?.showSizeChanger !== false && (
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold uppercase leading-none tracking-wide text-primary">
                Page size
              </span>
              <Select
                value={String(currentPagination?.pageSize ?? 10)}
                onValueChange={value =>
                  currentPagination?.onShowSizeChange?.(
                    currentPagination?.current ?? 1,
                    Number(value)
                  ) ?? currentPagination?.onChange?.(1, Number(value))
                }
              >
                <SelectTrigger className="h-8 w-[72px] border-slate-200/90 bg-white px-2.5 text-[13px] font-semibold text-slate-700 shadow-none hover:border-primary/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent usePortal>
                  {pageSizeOptions.map((opt: string) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <span className="pagination-items-count text-[13px] leading-snug text-primary">
            {currentPagination?.showTotal
              ? currentPagination.showTotal(total, range)
              : `${range[0]}-${range[1]} of ${total} items`}
          </span>
        </div>
      ),
      pageSizeOptions: pageSizeOptions,
      current: currentPagination?.current,
      total: currentPagination?.total,
      onChange: currentPagination?.onChange,
      onShowSizeChange: currentPagination?.onShowSizeChange,
      position: currentPagination?.position ?? ["bottomRight"],
      className: cn(
        "antd-pagination-balanced mt-3.5 px-5 py-1",
        paginationStyle?.paginationClassName
      ),
      itemRender: (_, type: string, originalElement: any) => {
        if (type === "prev") {
          return (
            <div className="flex items-center px-2 text-[12px] font-semibold text-slate-600 transition-colors hover:text-primary">
              Prev
            </div>
          );
        }
        if (type === "next") {
          return (
            <div className="flex items-center px-2 text-[12px] font-semibold text-slate-600 transition-colors hover:text-primary">
              Next
            </div>
          );
        }
        return originalElement;
      },
    };
  }, [pagination, paginationStyle]);

  // Memoize scroll config to handle empty state - prevent scrollbar when no data
  const tableScroll = useMemo(() => {
    if (!data || data.length === 0) {
      return { ...scroll, x: undefined };
    }
    return scroll;
  }, [data, scroll]);

  // Custom theme configuration for Fintech look
  const themeConfig = {
    token: {
      colorPrimary: primaryColor,
      borderRadius: 10,
      fontFamily: "var(--font-family, Inter, sans-serif)",
    },
    components: {
      Table: {
        headerBg: headerBg,
        headerColor: "#475569", // slate-600
        headerSplitColor: "transparent",
        rowHoverBg: "#F1F5F9", // slate-100
        cellPaddingBlock: 14,
        cellPaddingInline: 16,
        headerBorderRadius: 0,
        borderColor: "#F1F5F9",
      },
      Pagination: {
        itemActiveBg: primaryColor,
        colorPrimary: "#ffffff",
        colorPrimaryHover: primaryColor,
        borderRadius: 7,
        fontSize: 13,
      },
    },
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <div
        className={cn(
          "relative w-full overflow-hidden rounded-xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)]",
          containerClassName
        )}
      >
        {(showSettings || toolbarExtra) && (
          <div className="flex items-center justify-between border-b border-slate-50 px-6 py-3">
            <div className="flex flex-1 items-center gap-3">{toolbarExtra}</div>
            <div className="flex items-center gap-2">
              {showSettings && (
                <Popover
                  placement="bottomRight"
                  title={
                    <div className="flex items-center justify-between px-1 py-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          indeterminate={
                            visibleKeys.size > 0 &&
                            visibleKeys.size < columns.length
                          }
                          checked={visibleKeys.size === columns.length}
                          onChange={e => {
                            if (e.target.checked) {
                              setVisibleKeys(
                                new Set(
                                  columns.map(
                                    (c: any, i: number) =>
                                      (("dataIndex" in c
                                        ? c.dataIndex
                                        : c.key) ?? `col-${i}`) as string
                                  )
                                )
                              );
                            } else {
                              setVisibleKeys(new Set());
                            }
                          }}
                        />
                        <span className="text-sm font-semibold text-slate-700">
                          Column Display
                        </span>
                      </div>
                      <button
                        onClick={resetSettings}
                        className="flex items-center gap-1 text-[13px] font-medium text-primary transition-opacity hover:opacity-80"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Reset
                      </button>
                    </div>
                  }
                  content={
                    <div className="custom-scrollbar max-h-[300px] w-[240px] overflow-y-auto py-1">
                      {columns.map((col: any, index: number) => {
                        const key = (("dataIndex" in col
                          ? col.dataIndex
                          : col.key) ?? `col-${index}`) as string;
                        const isVisible = visibleKeys.has(key);
                        const isFixedLeft = fixedKeys.get(key) === "left";
                        const isFixedRight = fixedKeys.get(key) === "right";

                        const title =
                          typeof col.title === "function"
                            ? "Column"
                            : (col.title as string);

                        return (
                          <div
                            key={key}
                            className="group flex items-center justify-between rounded-md px-2 py-1.5 transition-colors hover:bg-slate-50"
                          >
                            <div className="flex items-center gap-2 overflow-hidden">
                              <GripVertical className="h-3.5 w-3.5 cursor-grab text-slate-300 opacity-0 transition-opacity active:cursor-grabbing group-hover:opacity-100" />
                              <Checkbox
                                checked={isVisible}
                                onChange={() => toggleVisibility(key)}
                              />
                              <span className="truncate text-[13px] text-slate-600">
                                {String(title)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Tooltip
                                title={
                                  isFixedLeft
                                    ? "Unpin from left"
                                    : "Pin to left"
                                }
                              >
                                <button
                                  onClick={() => toggleFixed(key, "left")}
                                  className={cn(
                                    "flex h-6 w-6 items-center justify-center rounded-md transition-all",
                                    isFixedLeft
                                      ? "bg-primary/10 text-primary"
                                      : "text-slate-300 hover:bg-slate-100 hover:text-slate-500"
                                  )}
                                >
                                  <Pin
                                    className={cn(
                                      "h-3.5 w-3.5 rotate-[-45deg]",
                                      isFixedLeft && "fill-current"
                                    )}
                                  />
                                </button>
                              </Tooltip>
                              <Tooltip
                                title={
                                  isFixedRight
                                    ? "Unpin from right"
                                    : "Pin to right"
                                }
                              >
                                <button
                                  onClick={() => toggleFixed(key, "right")}
                                  className={cn(
                                    "flex h-6 w-6 items-center justify-center rounded-md transition-all",
                                    isFixedRight
                                      ? "bg-primary/10 text-primary"
                                      : "text-slate-300 hover:bg-slate-100 hover:text-slate-500"
                                  )}
                                >
                                  <Pin
                                    className={cn(
                                      "h-3.5 w-3.5 rotate-[45deg]",
                                      isFixedRight && "fill-current"
                                    )}
                                  />
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  }
                  trigger="click"
                  overlayClassName="column-settings-popover"
                >
                  <Tooltip title="Table Settings">
                    <button className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:border-primary hover:text-primary active:scale-95">
                      <Settings className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </Popover>
              )}
            </div>
          </div>
        )}
        <Table<T>
          columns={transformedColumns as any}
          dataSource={data}
          rowKey={rowKey}
          loading={loading}
          pagination={paginationConfig}
          scroll={tableScroll}
          locale={{
            emptyText:
              locale?.emptyText ??
              (typeof emptyText === "string" ? (
                <div className="flex w-full flex-col items-center justify-center p-8 text-center">
                  <FileText
                    className="mb-4 h-12 w-12 text-slate-300"
                    strokeWidth={1}
                  />
                  <p className="font-medium text-slate-500">{emptyText}</p>
                </div>
              ) : (
                emptyText
              )),
          }}
          className={cn("antd-custom-table", className)}
          bordered={bordered}
          showHeader={showHeader}
          size={size}
          rowSelection={rowSelection}
          onRow={onRow}
          sticky={sticky}
        />

        {/* Custom global styles for table overrides that are hard to do via theme object */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          .antd-custom-table {
            font-family: var(--font-family, Inter, sans-serif) !important;
          }
          .antd-custom-table .ant-table-thead > tr > th {
            font-weight: 600;
            border-bottom: 1px solid #F1F5F9;
            font-family: var(--font-family, Inter, sans-serif) !important;
            vertical-align: top !important;
          }
          /* Force sorting and filter icons to align with the first line (text name) ONLY if input filter exists */
          .antd-custom-table .ant-table-thead > tr > th.header-with-filter .ant-table-column-sorters {
            align-items: flex-start !important;
          }
          .antd-custom-table .ant-table-thead > tr > th.header-with-filter .ant-table-column-sorter {
            margin-top: 2px !important;
          }
          .antd-custom-table .ant-table-thead > tr > th.header-with-filter .anticon.anticon-filter {
            margin-top: -47px !important;
            display: inline-block !important;
          }
          .antd-custom-table .ant-table-thead > tr > th.header-with-filter .ant-table-filter-trigger-container {
            align-self: flex-start !important;
          }
          .antd-custom-table .ant-table-tbody > tr > td {
            transition: background 0.2s ease;
            font-family: var(--font-family, Inter, sans-serif) !important;
          }
          /* Add horizontal spacing to the ends of the table */
          .antd-custom-table .ant-table-thead > tr > th:first-child:not(.ant-table-selection-column),
          .antd-custom-table .ant-table-tbody > tr > td:first-child:not(.ant-table-selection-column) {
            padding-left: 24px !important;
          }
          /* Explicitly set selection column padding to 0px and handle spacing via width/centering */
          .antd-custom-table .ant-table-selection-column {
            padding: 0px !important;
            width: 64px !important;
            min-width: 64px !important;
            text-align: center !important;
            vertical-align: middle !important;
          }
          /* Push header checkbox down to align with vertical flex text */
          .antd-custom-table .ant-table-thead > tr > th.ant-table-selection-column {
            padding-top: 16px !important;
          }
          /* Ensure the checkbox itself is properly positioned within the 0-padding cell */
          .antd-custom-table .ant-table-selection-column .ant-checkbox-wrapper {
            display: inline-flex !important;
            margin-left: 16px !important;
          }
          .antd-custom-table .ant-table-thead > tr > th:last-child,
          .antd-custom-table .ant-table-tbody > tr > td:last-child {
            padding-right: 24px !important;
            text-align: right;
          }
          .antd-custom-table .ant-table-pagination-left {
            margin: 12px 0 !important;
            padding: 0 20px !important;
            justify-content: flex-start !important;
          }
          .antd-custom-table .ant-table-pagination-right {
            margin: 12px 0 !important;
            padding: 0 20px !important;
            justify-content: flex-end !important;
          }
          .antd-custom-table .ant-pagination-item {
            border-radius: 7px;
            border: 1px solid #E2E8F0;
            transition: all 0.3s ease;
            font-family: var(--font-family, Inter, sans-serif) !important;
            font-size: 13px !important;
            margin: 0 3px !important;
            height: 28px !important;
            min-width: 28px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 1 !important;
          }
          .antd-custom-table .ant-pagination-item a {
            padding: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            height: 100% !important;
            width: 100% !important;
            font-size: 13px !important;
          }
          .antd-custom-table .ant-table-pagination {
            display: flex !important;
            align-items: center !important;
            position: relative !important;
            width: 100% !important;
            margin: 16px 0 12px 0 !important;
          }
          .antd-custom-table .ant-pagination {
            display: flex !important;
            align-items: center !important;
            width: 100% !important;
            justify-content: flex-end !important;
            padding: 0 20px !important;
            font-size: 13px !important;
          }
          .antd-custom-table .ant-pagination-total-text {
            height: 28px !important;
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
            margin-left: 14px !important;
            order: 10 !important;
            font-size: 13px !important;
          }
          .antd-custom-table .pagination-items-count {
            position: absolute !important;
            left: 20px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            margin: 0 !important;
          }
          .antd-custom-table .ant-pagination-prev {
            order: 1 !important;
          }
          .antd-custom-table .ant-pagination-item {
            order: 2 !important;
          }
          .antd-custom-table .ant-pagination-next {
            order: 3 !important;
          }
          .antd-custom-table .ant-pagination-options {
            order: 11 !important;
          }
          .antd-custom-table .ant-pagination-item:hover {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
          }
          .antd-custom-table .ant-pagination-item:hover a {
            color: #ffffff !important;
          }
          .antd-custom-table .ant-pagination-item-active {
            border-color: ${primaryColor} !important;
          }
          .antd-custom-table .ant-pagination-item-active a {
            color: #ffffff !important;
          }
          .antd-custom-table .ant-pagination-prev, 
          .antd-custom-table .ant-pagination-next {
            border-radius: 7px;
            margin: 0 5px !important;
            border: 1px solid #E2E8F0;
            background: white;
            min-width: 54px !important;
            height: 28px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            transition: all 0.3s ease;
          }
          .antd-custom-table .ant-pagination-prev:hover, 
          .antd-custom-table .ant-pagination-next:hover {
            border-color: ${primaryColor} !important;
            background-color: ${primaryColor}10 !important;
          }
          .antd-custom-table .ant-pagination-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            border-color: #F1F5F9 !important;
            background-color: #F8FAFC !important;
          }
          .antd-custom-table .ant-pagination-disabled div {
            color: #94A3B8 !important;
          }
          .antd-custom-table .ant-pagination-jump-prev,
          .antd-custom-table .ant-pagination-jump-next {
            border-radius: 6px;
            margin: 0 4px !important;
          }
          /* Custom horizontal scrollbar styling to match FinEase theme */
          .antd-custom-table .ant-table-content::-webkit-scrollbar,
          .antd-custom-table .ant-table-body::-webkit-scrollbar {
            height: 8px !important;
            width: 8px !important;
          }
          .antd-custom-table .ant-table-content::-webkit-scrollbar-track,
          .antd-custom-table .ant-table-body::-webkit-scrollbar-track {
            background: #F1F5F9 !important;
            border-radius: 10px !important;
          }
          .antd-custom-table .ant-table-content::-webkit-scrollbar-thumb,
          .antd-custom-table .ant-table-body::-webkit-scrollbar-thumb {
            background: ${primaryColor}80 !important;
            border-radius: 10px !important;
            border: 2px solid #F1F5F9 !important;
            transition: background 0.3s ease !important;
          }
          .antd-custom-table .ant-table-content::-webkit-scrollbar-thumb:hover,
          .antd-custom-table .ant-table-body::-webkit-scrollbar-thumb:hover {
            background: ${primaryColor} !important;
          }
          /* Firefox support */
          .antd-custom-table .ant-table-content,
          .antd-custom-table .ant-table-body {
            scrollbar-width: thin !important;
            scrollbar-color: ${primaryColor} #F1F5F9 !important;
          }
          /* Column settings popover styles */
          .column-settings-popover .ant-popover-inner {
            padding: 12px !important;
            border-radius: 12px !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px !important;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1 !important;
            border-radius: 10px !important;
          }
        `,
          }}
        />
      </div>
    </ConfigProvider>
  );
};

export default AntDTable;
